import { useState, useEffect, useCallback, useRef } from 'react';
import { parentEnterprisesService, ParentEnterprise } from '../services/parentEnterprisesService';

export type { ParentEnterprise } from '../services/parentEnterprisesService';

export interface UseParentEnterprisesParams {
  search?: string;
  page?: number;
  limit?: number;
  primarySortKey?: string;
  primarySortOrder?: 'asc' | 'desc' | null;
}

export type LoadingState =
  | 'idle'
  | 'initial'
  | 'search'
  | 'filter'
  | 'sort'
  | 'pagination';

export interface UseParentEnterprisesReturn {
  data: ParentEnterprise[] | null;
  loadingState: LoadingState;
  error: string | null;
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  refetch: () => void;
  setLoadingState: React.Dispatch<React.SetStateAction<LoadingState>>;
}

export const useParentEnterprises = (
  params: UseParentEnterprisesParams = {}
): UseParentEnterprisesReturn => {
  const [data, setData] = useState<ParentEnterprise[] | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>('initial');
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);

  const hasDataRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const {
    search = '',
    page = 1,
    limit = 10,
    primarySortKey = '',
    primarySortOrder = 'asc',
  } = params;

  const fetchParentEnterprises = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const currentController = new AbortController();
    abortControllerRef.current = currentController;

    const currentRequestId = ++requestIdRef.current;

    if (!hasDataRef.current) {
      setLoadingState('initial');
    }
    setError(null);

    try {
      const queryParams: Record<string, any> = {};

      if (search) queryParams.search = search;

      if (primarySortKey && primarySortOrder) {
        queryParams.primarySortKey = primarySortKey;
        queryParams.primarySortOrder = primarySortOrder;
      }

      queryParams.page = page;
      queryParams.limit = limit;

      const response = await parentEnterprisesService.getParentEnterprises(queryParams);

      if (currentRequestId !== requestIdRef.current) return;
      if (currentController.signal.aborted) return;

      const transformedData: ParentEnterprise[] = response?.data;
      const paginationData = (response as any)?.pagination;

      if (currentRequestId !== requestIdRef.current) return;

      setData(transformedData);
      setCurrentPage(paginationData.currentPage);
      setTotalCount(paginationData.totalCount);
      setTotalPages(paginationData.totalPages);
      setHasPrevPage(paginationData.hasPrevPage);
      setHasNextPage(paginationData.hasNextPage);
      hasDataRef.current = true;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      if (currentRequestId !== requestIdRef.current) return;

      setError(
        err instanceof Error ? err.message : 'Failed to fetch parent enterprises'
      );
      setData([]);
      setTotalCount(0);
      setHasNextPage(false);
      hasDataRef.current = true;
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoadingState('idle');
      }
    }
  }, [search, page, limit, primarySortKey, primarySortOrder]);

  useEffect(() => {
    fetchParentEnterprises();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchParentEnterprises]);

  return {
    data,
    loadingState,
    setLoadingState,
    totalPages,
    error,
    totalCount,
    currentPage,
    hasNextPage,
    hasPrevPage,
    refetch: fetchParentEnterprises,
  };
};